import { applySnapshot, cast, flow, types, getRoot } from 'mobx-state-tree';
import { CmsApi, CoreApi } from 'service/axios-api';
import { rootStore, IRootStore } from 'store/root';
import { ProductState, MapleStoryProductId } from 'types/enum';
import { FeedStore } from './feed';
import { ProductCms, ProductCmsPc } from './product-cms';
import { ProductLibrary, IProductLibrary } from './product-library';
import { NexonlinkStore } from './nexonlink';

const { win, coreappIpc } = window;

const AppStore = types
    .model('AppStore', {
        pc: types.array(ProductCmsPc),
        all: types.array(ProductCms),
        library: types.array(ProductLibrary),
        feeds: types.map(FeedStore),
        nexonlink: types.optional(NexonlinkStore, {}),
    })
    .views((self) => {
        const views = {
            get installedApps() {
                return self.library.filter((x) => !(x.installInfo.currentState & ProductState.NOT_INSTALLED));
            },
            get publicPC() {
                const list = self.all.filter((x) => x.productId && x.showInAllGamesPage);
                if (!views.IsKdAvailable) {
                    return list.filter((x) => x.productId && !views.root.regionConfig.isKd(x.productId));
                }
                return list;
            },
            get publicMobile() {
                return self.all.filter((x) => x.showInAllGamesPage && (x.android || x.ios));
            },
            get publicConsole() {
                return self.all.filter((x) => x.showInAllGamesPage && (x.xbox || x.psn));
            },
            // if there is a game in playing mode while streaming, it is referenced for disabling download and update buttons
            get streamingIngame(): boolean {
                return !!self.library.find((one) => one.streamingIngame);
            },
            get root(): IRootStore {
                return getRoot<IRootStore>(self);
            },
            get IsKdAvailable() {
                return false;
            },
        };
        return views;
    })
    .actions((self) => {
        const actions = {
            updateInstallInfos(data: any) {
                self.library.forEach((x) => {
                    if (data[x.productId]) x.installInfo = cast(data[x.productId]);
                });
            },
            fetchInstallInfos: flow(function* () {
                const keys = self.library.toJSON().reduce((prev: any, curr: IProductLibrary) => {
                    prev[curr.productId] = { name: curr.productName };
                    return prev;
                }, {});
                const d: any = yield coreappIpc.send('getInstalledApps', keys);
                actions.updateInstallInfos(d);
            }),
            updateInstallStatus(prodId: string, status: any) {
                const found = self.library.find((one) => one.productId === prodId);
                if (found) {
                    found.installStatus = cast({ streaming: '', ...status });
                    if (status === 'installed' && found.streamingIngame) found.setStreamingInGame(false); // SDS installation end
                }
            },
            fetchCDN: flow(function* (productId = '') {
                const res: any = yield CmsApi.get('/frontend/v3/{regionWebCode}/gamelist.json');
                res.map((x: any) => {
                    x.productId = x.productID;
                    delete x.productID;
                    return x;
                }); // convert productID -> productId

                if (self.root.systemInfo.onMac) {
                    const mapleInfo = res.filter((x: any) => x.productId === MapleStoryProductId);
                    if (!productId) {
                        self.all = cast(mapleInfo);
                    }

                    mapleInfo.forEach((one: any) => {
                        const found = self.pc.find((x) => x.productId === one.productId);
                        if (!self.feeds.get(one.productId)) self.feeds.set(one.productId, FeedStore.create({ productId: one.productId }));
                        if (!found) {
                            self.pc.push(cast({ ...one, feed: one.productId }));
                            const foundLib = self.library.find((x) => x.productId === one.productId);
                            if (foundLib) foundLib.cms = one.productId;
                        } else {
                            applySnapshot(found, { ...found, ...one });
                        }
                    });

                    return;
                }

                // all fetch
                if (!productId) {
                    self.all = cast(res);
                }

                res.filter((x: any) => x.productId && (!productId || x.productId === productId)).forEach((one: any) => {
                    const found = self.pc.find((x) => x.productId === one.productId);
                    if (!self.feeds.get(one.productId)) self.feeds.set(one.productId, FeedStore.create({ productId: one.productId }));
                    if (!found) {
                        self.pc.push(cast({ ...one, feed: one.productId }));
                        const foundLib = self.library.find((x) => x.productId === one.productId);
                        if (foundLib) foundLib.cms = one.productId;
                    } else {
                        applySnapshot(found, { ...found, ...one });
                    }
                });

                // self.pc.forEach((x: any) => {
                //     const pid = x.productId;
                //     if (!self.feeds.get(pid)) self.feeds.set(pid, FeedStore.create({ productId: pid }));
                //     if (pid) x.feed = pid;
                // });
            }),
            fetchLibrary: flow(function* () {
                const { data }: { data: [{ productId: string; productName: string }] } = yield CoreApi.get('/game-library/v1/library');

                if (self.root.systemInfo.onMac) {
                    data.filter((x) => x.productId === MapleStoryProductId).forEach((one) => {
                        const found = self.library.find((x) => x.productId === one.productId);
                        if (!found) {
                            self.library.push(cast({ ...one, cms: one.productId }));
                        }
                    });
                } else {
                    // don't overwrite existed self.library, it will result to loose all detail info
                    const HIDDEN_MAPLE_STORY_PRODUCT_IDS = ['59812', '59813'];
                    data.filter((x) => !HIDDEN_MAPLE_STORY_PRODUCT_IDS.includes(x.productId))
                        .filter((x) => !self.root.regionConfig.isKd(x.productId))
                        .forEach((one) => {
                            const found = self.library.find((x) => x.productId === one.productId);
                            if (!found) {
                                self.library.push(cast({ ...one, cms: one.productId }));
                            }
                        });
                }

                actions.fetchInstallInfos();
                if (win.name === 'main') {
                    const trayMenuGames = self.library.map((x) => ({
                        productId: x.productId,
                        displayName: x.displayName,
                        iconUrl: rootStore.regionConfig.cdnHost + x.cms?.trayMenuIcon,
                    }));
                    //console.log(trayMenuGames);
                    coreappIpc.send('setGamesOnTray', trayMenuGames);
                }
            }),
            nextAction(pid: string) {
                const one = self.library.find((x) => x.productId === pid) || self.pc.find((x) => x.productId === pid);
                return one && one.nextAction();
            },
        };
        return actions;
    });

export default AppStore;
